import { type FormEvent, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { criarVersao, PrecificacaoError } from '@/lib/precificacao';
import {
  derivarInicios,
  emptyFaixaRow,
  type FaixaRow,
  validarNovaVersao,
} from '@/lib/precificacao_form';
import { FaixaEditorRow, PRECIFICACAO_INPUT } from './FaixaEditorRow';
import { mapPrecificacaoError } from './messages';
import { Blueprint, Button } from '@/components/ui';

interface RowState extends FaixaRow {
  key: string;
}

let _seq = 0;
function newRow(): RowState {
  _seq += 1;
  return { key: `faixa-${_seq}`, ...emptyFaixaRow() };
}

/** A última faixa é sempre aberta: zera seu volume final para casar com o contrato. */
function comUltimaAberta(rows: RowState[]): RowState[] {
  const fim = rows.length - 1;
  return rows.map((row, i) => (i === fim ? { ...row, volumeFim: '' } : row));
}

interface NovaVersaoFormProps {
  bancoId: string;
  onCreated: () => void;
  /**
   * Avisa o pai enquanto a criação está em voo. O envio usa o `bancoId` do
   * instante do clique; sem trancar o seletor durante a requisição, a tela
   * passa a exibir outro banco e a versão comercial é gravada no histórico da
   * instituição errada (F-E51.F4-06).
   */
  onSubmittingChange: (submitting: boolean) => void;
}

export function NovaVersaoForm({ bancoId, onCreated, onSubmittingChange }: NovaVersaoFormProps) {
  const apiClient = useApiClient();
  const [vigenciaInicio, setVigenciaInicio] = useState('');
  const [vigenciaFim, setVigenciaFim] = useState('');
  const [rows, setRows] = useState<RowState[]>(() => [newRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const inicios = derivarInicios(rows);

  function marcarSubmissao(emVoo: boolean) {
    setSubmitting(emVoo);
    onSubmittingChange(emVoo);
  }

  function updateRow(index: number, patch: Partial<FaixaRow>) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const result = validarNovaVersao({ vigenciaInicio, vigenciaFim, faixas: rows });
    if (!result.ok) {
      setFormError(ptBR.precificacao[result.key]);
      return;
    }
    setFormError(null);
    marcarSubmissao(true);
    try {
      await criarVersao(apiClient, { banco_id: bancoId, ...result.built });
      setVigenciaInicio('');
      setVigenciaFim('');
      setRows([newRow()]);
      onCreated();
    } catch (err) {
      const reason = err instanceof PrecificacaoError ? err.reason : 'unknown';
      setFormError(mapPrecificacaoError(reason));
    } finally {
      marcarSubmissao(false);
    }
  }

  return (
    <Blueprint>
      <form
        onSubmit={handleSubmit}
        aria-label={ptBR.precificacao.novaVersaoTitle}
        className="flex flex-col gap-sc-3 p-sc-4"
      >
        <div>
          <h3 className="text-body-md font-semibold text-fg">
            {ptBR.precificacao.novaVersaoTitle}
          </h3>
          <p className="mt-1 text-body-sm text-fg-muted">{ptBR.precificacao.novaVersaoHint}</p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.precificacao.vigenciaInicioLabel}</span>
            <input
              type="date"
              value={vigenciaInicio}
              onChange={(e) => setVigenciaInicio(e.target.value)}
              data-testid="vigencia-inicio"
              className={PRECIFICACAO_INPUT}
            />
          </label>
          <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.precificacao.vigenciaFimLabel}</span>
            <input
              type="date"
              value={vigenciaFim}
              onChange={(e) => setVigenciaFim(e.target.value)}
              data-testid="vigencia-fim"
              className={PRECIFICACAO_INPUT}
            />
          </label>
        </div>

        <ul className="flex flex-col gap-2">
          {rows.map((row, index) => (
            <FaixaEditorRow
              key={row.key}
              index={index}
              row={row}
              inicio={inicios[index] ?? null}
              ultima={index === rows.length - 1}
              removivel={rows.length > 1}
              onChange={(patch) => updateRow(index, patch)}
              onRemove={() =>
                setRows((prev) => comUltimaAberta(prev.filter((_, i) => i !== index)))
              }
            />
          ))}
        </ul>

        <div className="flex items-center justify-between gap-2">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => setRows((prev) => comUltimaAberta([...prev, newRow()]))}
            data-testid="add-faixa"
          >
            {ptBR.precificacao.addFaixa}
          </Button>
          <Button type="submit" disabled={submitting} data-testid="submit-versao">
            {submitting ? ptBR.precificacao.submitting : ptBR.precificacao.submit}
          </Button>
        </div>

        {formError !== null ? (
          <p role="alert" data-testid="form-error" className="text-body-sm text-danger-700">
            {formError}
          </p>
        ) : null}
      </form>
    </Blueprint>
  );
}
